import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { KeyRound, Mail, ShieldCheck } from "lucide-react";
import type { PendingInvite } from "../../api/teams.js";
import { paths } from "../../app/paths.js";
import { useAcceptPendingInvite, useDeclineInvite, usePendingInvites } from "../../data/teams.js";
import { formatRelativeTime } from "../../lib/format.js";
import { ROLE_LABEL, describeTeamError } from "../../lib/teams.js";
import { useToast } from "../../ui/Toast.js";
import { Alert, Avatar, Badge, Button } from "../../ui/primitives.js";

/** Open invites for this account, shown above the team list. Renders nothing when there are none. */
export function PendingInvites(): ReactNode {
  const pending = usePendingInvites();
  const invites = pending.data ?? [];
  if (invites.length === 0) {
    return null;
  }
  return (
    <section aria-labelledby="pending-invites" className="mb-8">
      <h2 id="pending-invites" className="mb-3 flex items-center gap-2 text-sm font-semibold text-foam">
        <Mail size={15} aria-hidden="true" className="text-flow" />
        Offene Einladungen
        <span className="grid min-w-5 place-items-center rounded-full bg-flow px-1.5 text-[0.6875rem] font-semibold leading-5 text-ink">{invites.length}</span>
      </h2>
      <ul className="grid gap-4 md:grid-cols-2">
        {invites.map((invite: PendingInvite) => (
          <li key={invite.id}>
            <PendingInviteCard invite={invite} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function PendingInviteCard(props: { readonly invite: PendingInvite }): ReactNode {
  const { invite } = props;
  const navigate = useNavigate();
  const toast = useToast();
  const accept = useAcceptPendingInvite();
  const decline = useDeclineInvite();
  const [confirmDecline, setConfirmDecline] = useState(false);
  const needs2fa = !invite.stale && invite.require2fa && !invite.twoFactorReady;
  const error = accept.error ?? decline.error;

  return (
    <div className={`flex h-full flex-col rounded-xl border bg-ink-2 p-5 ${invite.stale ? "border-line opacity-80" : "border-flow/40"}`}>
      <div className="flex items-center gap-3">
        <Avatar label={invite.orgName} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-foam">{invite.orgName}</h3>
          <p className="truncate text-xs text-mist">
            {invite.invitedByEmail !== null ? `Eingeladen von ${invite.invitedByEmail} · ` : ""}
            läuft {formatRelativeTime(invite.expiresAt, Date.now())} ab
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        <Badge tone="neutral" icon={ShieldCheck}>{ROLE_LABEL[invite.role]}</Badge>
        {invite.stale ? <Badge tone="buoy" icon={KeyRound}>Ungültig</Badge> : null}
        {!invite.stale && invite.require2fa ? <Badge tone="buoy" icon={ShieldCheck}>2FA erforderlich</Badge> : null}
      </div>

      {needs2fa ? (
        <div className="mt-4">
          <Alert>
            Dieses Team verlangt 2FA. Ohne 2FA kannst du nach dem Beitritt nichts sehen.{" "}
            <Link to={`${paths.settings}?setup=totp`} className="text-flow underline-offset-4 hover:underline">
              Jetzt einrichten
            </Link>
          </Alert>
        </div>
      ) : null}
      {invite.stale ? (
        <p className="mt-4 text-sm leading-relaxed text-mist">
          Der Team-Schlüssel wurde erneuert, nachdem du eingeladen wurdest. Diese Einladung funktioniert nicht mehr.
          Bitte um eine neue Einladung.
        </p>
      ) : null}
      {!invite.stale && !invite.claimed ? (
        <p className="mt-4 text-sm leading-relaxed text-mist">
          Öffne den Einladungslink, den du bekommen hast, einmal in diesem Konto. Danach kannst du hier beitreten.
        </p>
      ) : null}
      {error !== null ? (
        <div className="mt-4">
          <Alert tone="danger">{describeTeamError(error)}</Alert>
        </div>
      ) : null}

      <div className="flex-1" />
      <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4">
        {invite.claimed && !invite.stale ? (
          <Button
            size="sm"
            loading={accept.isPending}
            onClick={(): void => {
              accept.mutate(invite, {
                onSuccess: (joined): void => {
                  toast.notify(`Willkommen im Team ${joined.orgName}.`, { tone: "success" });
                  void navigate(paths.team(joined.orgId));
                }
              });
            }}
          >
            Beitreten
          </Button>
        ) : null}
        <Button
          size="sm"
          variant={confirmDecline && !invite.stale ? "danger" : "ghost"}
          loading={decline.isPending}
          onClick={(): void => {
            if (!confirmDecline && !invite.stale) {
              setConfirmDecline(true);
              return;
            }
            decline.mutate(invite.id, {
              onSuccess: (): void => toast.notify(invite.stale ? "Ungültige Einladung entfernt." : `Einladung zu ${invite.orgName} abgelehnt.`)
            });
          }}
        >
          {invite.stale ? "Entfernen" : confirmDecline ? "Wirklich ablehnen?" : "Ablehnen"}
        </Button>
      </div>
    </div>
  );
}
